/* @jsx h */
import { defineComponent, Fragment, h } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";

export type MenuListItemProps = {
  to?: string;
  icon: string;
  title: string;
  badge?: number;
  iconClass?: string;
  external?: boolean;
  onClick?: Function;
};

const rowContentClass =
  "flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-base-content/5";

export const MenuListItem = defineComponent<MenuListItemProps>({
  name: "MenuListItem",
  props: ["to", "icon", "title", "badge", "iconClass", "external", "onClick"],
  setup(props) {
    const route = useRoute();
    const { t } = useLocalization();

    const inner = () => (
      <Fragment>
        <span
          class={`material-symbols-outlined flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-base-content/5 text-[19px] ${props.iconClass ?? "text-base-content/70"}`}
        >
          {props.icon}
        </span>
        <span class="min-w-0 flex-1 truncate text-[14.5px] font-medium text-base-content/90">
          {t(props.title)}
        </span>
        {props.badge && props.badge > 0 ? (
          <span class="inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-error px-1.5 text-[11px] font-semibold text-error-content">
            {props.badge > 99 ? "99+" : props.badge}
          </span>
        ) : null}
        <span class="material-symbols-outlined shrink-0 text-[18px] text-base-content/25">
          {props.external ? "open_in_new" : "chevron_right"}
        </span>
      </Fragment>
    );

    return () => {
      if (!props.to) {
        return (
          <button
            type="button"
            class={rowContentClass}
            onClick={(e: MouseEvent) => props.onClick?.(e)}
          >
            {inner()}
          </button>
        );
      }

      if (props.external) {
        return (
          <a
            href={props.to}
            class={rowContentClass}
            onClick={(e: MouseEvent) => props.onClick?.(e)}
          >
            {inner()}
          </a>
        );
      }

      const isActive = route.path === props.to;

      return (
        <RouterLink
          to={props.to}
          class={`${rowContentClass} ${isActive ? "bg-base-content/5" : ""}`}
          onClick={(e: MouseEvent) => props.onClick?.(e)}
        >
          {inner()}
        </RouterLink>
      );
    };
  },
});
